import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app'
import { Auth, getAuth, signInWithCustomToken } from 'firebase/auth'
import {
  Database,
  DatabaseReference,
  getDatabase,
  onChildAdded,
  onValue,
  push,
  ref,
  serverTimestamp
} from 'firebase/database'

/**
 * Cliente do Realtime Database para o chat de atendimento (spec 018).
 *
 * Importação modular de propósito (`firebase/app`, `firebase/auth`,
 * `firebase/database`): o bundler inclui só o que é usado. Importar
 * `firebase/compat` ou o pacote inteiro multiplicaria o tamanho do bundle.
 *
 * A autenticação NÃO é do Firebase: o backend valida a sessão Cannect e emite
 * um custom token com os claims `userId` e `role`, que as regras do RTDB leem.
 */

const APP_NAME = 'cannect-support-chat'

const config = {
  apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_APP_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_APP_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_APP_FIREBASE_APP_ID
}

export const isFirebaseConfigured = () => Boolean(config.apiKey && config.databaseURL)

let app: FirebaseApp | null = null

const getFirebaseApp = (): FirebaseApp => {
  if (app) return app

  if (!isFirebaseConfigured()) {
    throw new Error('Firebase não configurado: verifique as variáveis VITE_APP_FIREBASE_*')
  }

  app = getApps().some((item) => item.name === APP_NAME) ? getApp(APP_NAME) : initializeApp(config, APP_NAME)

  return app
}

export const getFirebaseAuth = (): Auth => getAuth(getFirebaseApp())

export const getFirebaseDatabase = (): Database => getDatabase(getFirebaseApp())

/** Autentica com o custom token emitido pelo backend. Idempotente. */
export const signInWithBackendToken = async (customToken: string) => {
  const auth = getFirebaseAuth()

  if (auth.currentUser) return auth.currentUser

  const credential = await signInWithCustomToken(auth, customToken)

  return credential.user
}

const conversationRef = (uuid: string, suffix = ''): DatabaseReference =>
  ref(getFirebaseDatabase(), `conversations/${uuid}${suffix}`)

/**
 * Escuta as mensagens de uma conversa.
 *
 * `onChildAdded` dispara uma vez por mensagem já existente e depois a cada nova
 * — é o que permite carregar o histórico e continuar recebendo com um listener
 * só. Devolve a função de cancelamento; chamar no cleanup do efeito é
 * obrigatório, senão o listener sobrevive à troca de conversa.
 */
export const subscribeToMessages = (uuid: string, onMessage: (message: any) => void) =>
  onChildAdded(conversationRef(uuid, '/messages'), (snapshot) => {
    const value = snapshot.val()
    if (value) onMessage({ id: snapshot.key, ...value })
  })

/** Escuta o estado da conversa (status, posição na fila, nome do atendente). */
export const subscribeToMeta = (uuid: string, onMeta: (meta: any) => void) =>
  onValue(conversationRef(uuid, '/meta'), (snapshot) => onMeta(snapshot.val()))

export type TOutgoingMessage = {
  senderType: 'patient'
  senderId: number
  senderName: string | null
  content: string
  attachments?: { url: string; format?: string; size_bytes?: number }[]
}

/**
 * Publica a mensagem direto no RTDB — é isso que dá o tempo real sem passar
 * pelo backend. As regras validam que o remetente é quem diz ser.
 */
export const pushMessage = async (uuid: string, message: TOutgoingMessage) => {
  const payload: Record<string, unknown> = {
    sender_type: message.senderType,
    sender_id: message.senderId,
    sender_name: message.senderName,
    content: message.content,
    created_at: serverTimestamp()
  }

  if (message.attachments?.length) {
    payload.attachments = message.attachments.reduce<Record<string, unknown>>((acc, attachment, index) => {
      acc[`a${index}`] = {
        url: attachment.url,
        format: attachment.format ?? null,
        size_bytes: attachment.size_bytes ?? null
      }

      return acc
    }, {})
  }

  return push(conversationRef(uuid, '/messages'), payload)
}
